import { useState, useEffect, useRef } from "react";
import { X, Plus, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Inline-editable chip list. Used on StreamDetail for the four "suggested_*"
 * discovery accelerators. Soft-discovery hints — not hard restrictions.
 *
 * Props:
 *   streamId        — required
 *   field           — "suggested_communities" | "suggested_creators" | "suggested_hashtags" | "suggested_search_phrases"
 *   value           — current array of strings
 *   label           — overline text
 *   placeholder     — input placeholder
 *   prefix          — optional display prefix (e.g. "#" for hashtags)
 *   onSaved         — callback(nextArray) after save
 *   testid          — base data-testid (e.g. "chips-communities")
 */
export default function EditableChips({
    streamId,
    field,
    value = [],
    label,
    placeholder = "Add…",
    prefix = "",
    onSaved,
    testid,
}) {
    const [items, setItems] = useState(value);
    const [draft, setDraft] = useState("");
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { if (!editing) setItems(value); }, [value, editing]);

    useEffect(() => {
        if (editing && inputRef.current) inputRef.current.focus();
    }, [editing]);

    const addDraft = () => {
        const v = draft.trim().replace(/^#/, "");  // strip leading # if user typed one
        if (!v) return;
        if (items.includes(v)) {
            setDraft("");
            return;
        }
        setItems([...items, v]);
        setDraft("");
    };

    const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/streams/${streamId}`, { [field]: items });
            toast.success(`${label} updated`);
            setEditing(false);
            onSaved?.(items);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        setItems(value);
        setDraft("");
        setEditing(false);
    };

    const onKey = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addDraft();
        } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
        }
    };

    if (!editing) {
        return (
            <div data-testid={testid}>
                <div className="flex items-center gap-2 mb-2">
                    <div className="overline text-[9px]">{label}</div>
                    <button
                        type="button"
                        data-testid={`${testid}-edit`}
                        onClick={() => setEditing(true)}
                        className="ml-auto text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-primary transition-colors"
                    >
                        Edit
                    </button>
                </div>
                {items.length === 0 ? (
                    <p className="text-[11px] italic text-muted-foreground">None yet — click Edit to add.</p>
                ) : (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px] text-muted-foreground">
                        {items.map((c) => (
                            <span key={c} data-testid={`${testid}-item`}>{prefix}{c}</span>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div data-testid={testid} className="rounded-sm border border-primary/30 bg-primary/[0.02] p-3 -m-3">
            <div className="flex items-center gap-2 mb-2">
                <div className="overline text-[9px] text-primary">{label}</div>
                <span className="ml-auto text-[10px] font-mono text-muted-foreground">enter to add · esc to cancel</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {items.map((c, i) => (
                    <span
                        key={c}
                        data-testid={`${testid}-chip`}
                        className="inline-flex items-center gap-1 font-mono text-[11px] bg-background border border-border px-2 py-0.5 rounded-sm"
                    >
                        {prefix}{c}
                        <button
                            type="button"
                            data-testid={`${testid}-remove`}
                            onClick={() => remove(i)}
                            className="text-muted-foreground hover:text-accent"
                            aria-label={`Remove ${c}`}
                        >
                            <X size={10} weight="bold" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1.5">
                <input
                    ref={inputRef}
                    data-testid={`${testid}-input`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder={placeholder}
                    className="flex-1 font-mono text-[11px] px-2 py-1 rounded-sm border border-border bg-background focus:outline-none focus:border-primary"
                />
                <button
                    type="button"
                    data-testid={`${testid}-add`}
                    onClick={addDraft}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border border-border hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1"
                >
                    <Plus size={10} weight="bold" /> Add
                </button>
            </div>
            <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-border">
                <button
                    type="button"
                    data-testid={`${testid}-cancel`}
                    onClick={cancel}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 text-muted-foreground hover:text-foreground"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    data-testid={`${testid}-save`}
                    onClick={save}
                    disabled={saving}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm bg-foreground text-background hover:opacity-90 inline-flex items-center gap-1 disabled:opacity-50"
                >
                    <Check size={10} weight="bold" /> {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}
